import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { useTab } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { type DatabaseView } from '@databases/types/database/view';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { getLayoutIcons } from '@databases/view-settings/utils';
import { type ViewPanelSettingType } from '@databases/views/types';
import React, {
  createContext,
  type FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import ViewActions from './ViewActions';
import ViewNameWithTruncation from './ViewNameWithTruncation';

const TabInlineStyles = xcss({
  paddingLeft: 'space.075',
  paddingRight: 'space.100',
  alignItems: 'center',
});

const ActiveTabStyles = xcss({
  paddingRight: 'space.0',
});

const CustomTabStyles = xcss({
  // this is needed to make sure the drop indicator doesn't get cut off
  overflow: 'visible !important',
});

export const InstanceIdContext = createContext<symbol | null>(null);

export type DraggableViewTabProps = {
  contentId: string;
  view: DatabaseView;
  isDisabled?: boolean;
  isActive?: boolean;
  isDefault: boolean;
  isReadonly?: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  handleClose?: () => void;
  showViewSettings?: boolean;
  savedViews: DatabaseView[];
  handleReorder: (idx: number, newIdx: number) => void;
  closestEdge: Edge | null;
  setClosestEdge: (edge: Edge | null) => void;
  draggedOverIndex: number | null;
  setDraggedOverIndex: (index: number | null) => void;
  index: number;
  isUnsavedView?: boolean;
  isRightmostTab: boolean;
  rightmostTabIndex?: number;
};

type ItemData = {
  type: 'tab';
  view: DatabaseView;
  index: number;
  instanceId: symbol | null;
};

const getItemData = (
  view: DatabaseView,
  index: number,
  instanceId: symbol | null
): ItemData => {
  return {
    type: 'tab',
    view,
    index,
    instanceId,
  };
};

export const DraggableViewTab: FunctionComponent<DraggableViewTabProps> = memo(
  function DraggableViewTab({
    view,
    contentId,
    isActive,
    isDefault,
    isReadonly,
    openViewSettingsPanel,
    showViewSettings = true,
    savedViews,
    handleReorder,
    closestEdge,
    setClosestEdge,
    draggedOverIndex,
    setDraggedOverIndex,
    index,
    isUnsavedView,
    isRightmostTab,
    rightmostTabIndex,
  }) {
    const [isViewActionsOpen, setIsViewActionsOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const instanceId = useContext(InstanceIdContext);
    const [isDragging, setIsDragging] = useState(false);
    const showViewSettingAction = isPluginEnabled(PluginKey.VIEW_SETTINGS_ACTIONS);
    useEffect(() => {
      if (isUnsavedView || isReadonly) {
        return;
      }
      const el = ref.current!;
      const data = getItemData(view, index, instanceId);
      return combine(
        draggable({
          element: el,
          getInitialData: () => data,
          onDragStart: () => setIsDragging(true),
          onDrop: () => setIsDragging(false),
        }),
        dropTargetForElements({
          element: el,
          getData({ input }) {
            return attachClosestEdge(data, {
              element: el,
              input,
              allowedEdges: ['left', 'right'],
            });
          },
          getIsSticky: () => true,
          canDrop: ({ source }) =>
            source.data.instanceId === instanceId &&
            source.data.type === 'tab' &&
            source.data.content !== view,
          onDragEnter: () => {},
          onDragLeave: () => setClosestEdge(null),
          onDrop: () => setClosestEdge(null),
          onDrag({ self, source }) {
            const isSource = source.element === el;
            if (isSource) {
              setClosestEdge(null);
              return;
            }

            const closestEdge = extractClosestEdge(self.data);
            setDraggedOverIndex(index);

            const sourceIndex = source.data.index;
            if (typeof sourceIndex !== 'number') {
              return;
            }

            const isItemBeforeSource = index === sourceIndex - 1;
            const isItemAfterSource = index === sourceIndex + 1;

            const isDropIndicatorHidden =
              (isItemBeforeSource && closestEdge === 'right') ||
              (isItemAfterSource && closestEdge === 'left');

            if (isDropIndicatorHidden) {
              setClosestEdge(null);
              return;
            }

            setClosestEdge(closestEdge);
          },
        })
      );
    }, [
      index,
      instanceId,
      setClosestEdge,
      setDraggedOverIndex,
      isUnsavedView,
      view,
      isReadonly,
    ]);

    useHandleOutsideClick(() => setIsViewActionsOpen(false), undefined, [
      '[data-testid="view-actions-dropdown-menu--content"]',
      '[data-testid="view-actions-dropdown-menu--trigger"]',
    ]);

    const LayoutIcon = useCallback(
      () =>
        getLayoutIcons({
          type: view.settings.layout.type,
          iconSize: 'medium',
          isDefault,
          isPresentationOnly: false,
          isUnsavedView,
        }).icon,
      [isDefault, isUnsavedView, view.settings.layout.type]
    );

    const tabAttributes = useTab();

    return (
      <Box ref={ref} xcss={CustomTabStyles} {...tabAttributes}>
        {/* Needed as a node for drop indicator to render on, otherwise clips */}
        <Box
          xcss={xcss({
            paddingBottom: 'space.025',
            paddingTop: 'space.025',
            opacity: isDragging ? '0.4' : '1',
            position: 'relative',
            ':hover': {
              backgroundColor: 'color.background.neutral',
            },
          })}
        >
          <Inline
            xcss={[
              TabInlineStyles,
              showViewSettingAction && isActive && showViewSettings && ActiveTabStyles,
            ]}
            space="space.100"
          >
            <LayoutIcon />
            <ViewNameWithTruncation view={view} isDragging={isDragging} />
            {showViewSettings && isActive && showViewSettingAction && (
              <ViewActions
                contentId={contentId}
                view={view}
                isViewActive={isActive}
                isOpen={isViewActionsOpen}
                setIsViewActionsOpen={setIsViewActionsOpen}
                isDefault={isDefault}
                isReadonly={isReadonly}
                openViewSettingsPanel={openViewSettingsPanel}
                isTabMode={true}
                savedViews={savedViews}
                handleReorder={handleReorder}
                isUnsavedView={isUnsavedView}
                isRightmostTab={isRightmostTab}
                rightmostTabIndex={rightmostTabIndex}
              />
            )}
          </Inline>
          {!isUnsavedView && draggedOverIndex === index && closestEdge && (
            <Box testId="drop-indicator">
              <DropIndicator
                edge={closestEdge}
                gap={
                  // For first and last elem in tab list, lessen gap to prevent
                  // overflow
                  isRightmostTab && closestEdge === 'right'
                    ? token('space.100', '8px')
                    : index === 0 && closestEdge === 'left'
                    ? token('space.negative.100', '-8px')
                    : token('space.200', '16px')
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);
